"use client";
import { FC, HTMLProps, forwardRef } from "react";

// libs
import classNames from "classnames";

// components
import { Error } from "@/components/common";
import { useTheme } from "@/context/Theme.context";
import { Theme } from "@/enums";

type HiddenRadioButtonProps = {
  id: string | number;
  label?: string;
  name: string;
  error?: string | boolean;
  value?: string | number;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  infoContent?: any;
  iconPosition?: "left" | "right";
  additionalLabel?: string;
} & HTMLProps<HTMLInputElement>;

const HiddenRadioButton: FC<HiddenRadioButtonProps> = forwardRef<
  HTMLInputElement,
  HiddenRadioButtonProps
>(
  (
    {
      id,
      label = "",
      value,
      name,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      icon,
      infoContent,
      iconPosition = "right",
      additionalLabel,
      width = "w-full",
      ...props
    },
    ref,
  ) => {
    const inputClasses = classNames("w-0", "h-0", className);

    const theme = useTheme();

    return (
      <div
        className={classNames(
          "relative rounded-[35px] border-[2px]",
          {
            "border-red-600": error,
            "border-[#13CFE8] bg-[#13cfe81a]":
              props.checked && theme !== Theme.UK,
            "border-[#FF6500] bg-[#FFF0E6]":
              props.checked && theme === Theme.UK,
            "border-transparent bg-[#F9F9F9]": !props.checked,
          },

          containerClassName,
          width,
        )}
      >
        <div
          className={classNames(
            "input-wrapper relative w-full bg-transparent",
            wrapperClassName,
          )}
        >
          <label
            className={classNames(
              "flex h-full cursor-pointer items-center justify-start p-[12px] py-[13px] text-xs font-bold text-black",
              labelClassName,
            )}
            htmlFor={id}
          >
            {icon && (
              <div className="mr-[12px] flex h-[28px] min-h-[28px] w-[28px] min-w-[28px] items-center justify-center rounded-[50%] bg-[#f9f9f9]">
                {icon}
              </div>
            )}
            <div>
              <span
                className="block text-base font-semibold uppercase text-black"
                dangerouslySetInnerHTML={{ __html: label }}
              />
              {additionalLabel && (
                <span
                  className="mt-[4px] text-sm font-normal text-black opacity-60"
                  dangerouslySetInnerHTML={{ __html: additionalLabel }}
                />
              )}
            </div>
            <div className="relative ml-auto flex">
              <input
                id={id}
                ref={ref}
                className={classNames(
                  "relative z-10 cursor-pointer pt-4 opacity-0",
                  inputClasses,
                )}
                name={name}
                disabled={disabled}
                maxLength={maxLength}
                type="radio"
                placeholder=""
                value={value || ""}
                checked={props.checked}
                {...props}
              />
            </div>
          </label>
        </div>

        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

HiddenRadioButton.displayName = "HiddenRadioButton";

export { HiddenRadioButton };
